import React, { Suspense } from 'react';
import {
  AppBar,
  Container,
  Toolbar,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Sync as SyncIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authClient } from "../lib/auth-client";
import { useNavigate } from "react-router-dom";
import { ADMIN_ROLES } from '../lib/constants';
import { tenant } from '../lib/branding';
import { apiFetch } from '../lib/api-fetch';
import { formatCurrency } from '../lib/formatters';
import { keys } from '../lib/query-keys';
import { clearPrivateRuntimeCaches, clearQueryPersistence } from '../lib/query-persistence';
import { useTheme } from '@mui/material/styles';
import { useOrganization } from '../contexts/OrganizationContext';
import { Star as StarIcon } from '@mui/icons-material';

const NotificationDropdown = React.lazy(() => import('./NotificationDropdown'));

const getTodayMetricsRange = () => {
  const now = new Date();
  const from = new Date(now);
  const to = new Date(now);

  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};

const Navbar = ({ session }) => {
  const theme = useTheme();
  const isLightMode = theme.palette.mode === 'light';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [accountMenuAnchor, setAccountMenuAnchor] = React.useState(null);
  const [profitMenuAnchor, setProfitMenuAnchor] = React.useState(null);

  // Organization Context
  const orgContext = useOrganization() || {};
  const { activeOrganization, subscription, isSoloBarber, activeBarbershopId, hasFeature } = orgContext;

  const userRole = session?.role?.toLowerCase();
  const orgRole = activeOrganization?.role?.toLowerCase();
  const isManager = Boolean(
    ADMIN_ROLES.includes(userRole) ||
    ADMIN_ROLES.includes(orgRole) ||
    isSoloBarber
  );

  const [metricFetchUserId, setMetricFetchUserId] = React.useState(null);

  const isFreePlan = !subscription || subscription?.plan?.name === 'Gratis';

  const todayRange = React.useMemo(() => getTodayMetricsRange(), []);

  const showOrgMetrics = hasFeature?.('organization_metrics');
  const isOrgOnly = activeBarbershopId === null;
  const useOrgMetricsInNavbar = Boolean(isOrgOnly && showOrgMetrics && activeOrganization?.id);

  const metricQueryKey = React.useMemo(
    () => useOrgMetricsInNavbar
      ? keys.metrics.orgSummary(activeOrganization.id, todayRange.from, todayRange.to)
      : keys.metrics.summary(session?.user?.id, todayRange.from, todayRange.to),
    [session?.user?.id, todayRange.from, todayRange.to, useOrgMetricsInNavbar, activeOrganization?.id],
  );
  const cachedTodayMetrics = queryClient.getQueryData(metricQueryKey);
  const shouldFetchMetric = !!session?.user?.id && metricFetchUserId === session.user.id && (activeBarbershopId !== null || useOrgMetricsInNavbar);

  React.useEffect(() => {
    if (!session?.user?.id) return undefined;

    const timeoutMs = cachedTodayMetrics ? 700 : 180;
    const timeoutId = window.setTimeout(() => {
      setMetricFetchUserId(session.user.id);
    }, timeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [cachedTodayMetrics, session?.user?.id]);

  const { data: todayMetricsData } = useQuery({
    queryKey: metricQueryKey,
    queryFn: () => {
      const params = new URLSearchParams({
        from: todayRange.from,
        to: todayRange.to,
      });
      if (useOrgMetricsInNavbar) {
        return apiFetch(`/organizations/${activeOrganization.id}/metrics/summary?${params.toString()}`);
      }
      return apiFetch(`/metrics/summary?${params.toString()}`);
    },
    enabled: !!session?.user?.id && shouldFetchMetric,
    initialData: () => queryClient.getQueryData(metricQueryKey),
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const todayMetrics = todayMetricsData?.data || todayMetricsData;
  const primaryAmount = isManager
    ? todayMetrics?.profit?.total
    : todayMetrics?.commissions?.total;
  const trend = isManager
    ? todayMetrics?.profit?.trend
    : todayMetrics?.commissions?.trend;
  const formatted = primaryAmount !== undefined && primaryAmount !== null
    ? formatCurrency(primaryAmount)
    : null;
  const todayMetric = {
    sign: '$',
    signColor:
      primaryAmount > 0 ? '#34d399'
        : primaryAmount < 0 ? '#f87171'
          : 'text.secondary',
    value: formatted ? formatted.replace(/^\$/, '') : '--',
    delta: null,
    deltaColor: trend === 'down' ? '#f87171' : trend === 'flat' ? '#94a3b8' : '#10b981',
    label: isManager ? 'Ganancia neta' : 'En comisiones',
  };

  const handleForceRefresh = async () => {
    await queryClient.refetchQueries();
  };

  const handleLogout = async () => {
    try {
      await authClient.signOut();

      queryClient.clear();
      await Promise.allSettled([
        clearQueryPersistence(),
        clearPrivateRuntimeCaches(),
      ]);

      window.location.href = "/";
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const handleOpenAccountMenu = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const handleOpenProfitMenu = (event) => {
    setProfitMenuAnchor(event.currentTarget);
  };

  const handleCloseProfitMenu = () => {
    setProfitMenuAnchor(null);
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pt: 'env(safe-area-inset-top)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backdropFilter: isLightMode ? 'blur(12px)' : 'blur(18px)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0 }, minHeight: { xs: 60, md: 66 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              onClick={() => navigate('/home')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            >
              {tenant.isRivas ? (
                <img
                  src={tenant.logo(theme.palette.mode)}
                  alt="Logo"
                  style={{ maxHeight: '36px' }}
                />
              ) : (
                <Typography
                  variant="h5"
                  sx={{
                    fontStyle: 'italic',
                    fontWeight: 700,
                    color: 'primary.main',
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                    fontSize: '1.35rem'
                  }}
                >
                  {tenant.name}
                </Typography>
              )}
            </Box>

            {isFreePlan && isManager && activeOrganization && (
              <Box
                onClick={() => navigate('/saas/pricing')}
                sx={{
                  ml: 2,
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 4,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: 'warning.dark',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.warning.main, 0.3),
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                  }
                }}
              >
                <StarIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Mejorar Plan</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1.5 } }}>
            <Box
              onClick={handleOpenProfitMenu}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                cursor: 'pointer',
                flexShrink: 0,
                borderRadius: '8px',
                border: `1px solid ${alpha('#10b981', isLightMode ? 0.2 : 0.15)}`,
                bgcolor: alpha('#10b981', isLightMode ? 0.07 : 0.02),
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: alpha('#10b981', isLightMode ? 0.12 : 0.06),
                  borderColor: alpha('#10b981', isLightMode ? 0.42 : 0.35),
                  transform: 'translateY(-0.5px)',
                  boxShadow: `0 2px 8px -2px ${alpha('#10b981', isLightMode ? 0.22 : 0.15)}`
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 620,
                  color: 'text.primary',
                  letterSpacing: '-0.01em',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <Box component="span" sx={{ color: '#10b981', mr: '2px', fontWeight: 680 }}>
                  {todayMetric.sign}
                </Box>
                {todayMetric.value}
              </Typography>
              {todayMetric.delta ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: todayMetric.deltaColor,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    display: { xs: 'none', sm: 'inline' },
                    opacity: 0.8
                  }}
                >
                  {todayMetric.delta}
                </Typography>
              ) : null}
            </Box>

            <Menu
              anchorEl={profitMenuAnchor}
              open={Boolean(profitMenuAnchor)}
              onClose={handleCloseProfitMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 160,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.background.paper, isLightMode ? 0.92 : 0.78),
                    backdropFilter: isLightMode ? 'blur(8px)' : 'blur(12px)',
                    boxShadow: isLightMode
                      ? `0 18px 40px ${alpha(theme.palette.primary.main, 0.16)}`
                      : '0 8px 32px rgba(0, 0, 0, 0.4)',
                    backgroundImage: 'none',
                  },
                }
              }}
            >
              <Box sx={{ px: 1.5, py: 1.25, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 520, color: 'text.secondary', display: 'block', mb: 1, fontSize: '0.75rem' }}>
                  {isManager ? 'Ganancias de hoy' : 'Comisiones de hoy'}
                </Typography>
                <MenuItem
                  onClick={() => {
                    handleCloseProfitMenu();
                    navigate('/stats?range=today');
                  }}
                  sx={{
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: 'primary.main',
                    py: 0.5,
                    minHeight: 'auto',
                    justifyContent: 'center',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, isLightMode ? 0.1 : 0.05) }
                  }}
                >
                  Ver más
                </MenuItem>
              </Box>
            </Menu>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Sincronizar Datos">
                <IconButton
                  onClick={handleForceRefresh}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, isLightMode ? 0.12 : 0.08) },
                    '& svg': { stroke: 'currentColor', strokeWidth: 1.2, fontSize: 20 }
                  }}
                >
                  <SyncIcon />
                </IconButton>
              </Tooltip>
              <Suspense fallback={<Box sx={{ width: 40, height: 40 }} />}>
                <NotificationDropdown />
              </Suspense>
              <Tooltip title={session?.user?.email || 'Cuenta'}>
                <IconButton
                  onClick={handleOpenAccountMenu}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    ml: 0.5,
                    border: '1px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, isLightMode ? 0.12 : 0.08),
                      borderColor: alpha(theme.palette.primary.main, isLightMode ? 0.26 : 0.2)
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: alpha(theme.palette.primary.main, isLightMode ? 0.14 : 0.12),
                      color: 'primary.main',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      boxShadow: isLightMode
                        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`
                        : '0 0 0 1px rgba(255,255,255,0.05)'
                    }}
                  >
                    {(session?.user?.email || session?.user?.name || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
            <Menu
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={handleCloseAccountMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.background.paper, isLightMode ? 0.92 : 0.78),
                    backdropFilter: isLightMode ? 'blur(8px)' : 'blur(12px)',
                    boxShadow: isLightMode
                      ? `0 18px 40px ${alpha(theme.palette.primary.main, 0.16)}`
                      : '0 8px 32px rgba(0, 0, 0, 0.4)',
                    backgroundImage: 'none',
                  },
                }
              }}
            >
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.68, mb: 0.25, fontWeight: 460 }}>
                  Sesión iniciada
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                  }}
                >
                  {session?.user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleCloseAccountMenu();
                  navigate('/settings');
                }}
                sx={{ py: 1.1, fontWeight: 500 }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Configuración
              </MenuItem>
              {isManager && (
                <MenuItem
                  onClick={() => {
                    handleCloseAccountMenu();
                    navigate('/saas/settings');
                  }}
                  sx={{ py: 1.1, fontWeight: 500 }}
                >
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" />
                  </ListItemIcon>
                  {isSoloBarber ? 'Mi barbería' : 'Mi organización'}
                </MenuItem>
              )}
              {isManager && (
                <MenuItem
                  onClick={() => {
                    handleCloseAccountMenu();
                    navigate('/saas/dashboard');
                  }}
                  sx={{ py: 1.1, fontWeight: 500 }}
                >
                  <ListItemIcon>
                    <CreditCardIcon fontSize="small" />
                  </ListItemIcon>
                  Mi suscripción
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleCloseAccountMenu();
                  navigate('/settings#ayuda');
                }}
                sx={{ py: 1.1, fontWeight: 500 }}
              >
                <ListItemIcon>
                  <HelpOutlineIcon fontSize="small" />
                </ListItemIcon>
                Ayuda
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  handleCloseAccountMenu();
                  await handleLogout();
                }}
                sx={{ py: 1.1, fontWeight: 500 }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Salir
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
